import { CloudStagingListView } from '../view/CloudStagingListView';
import { templateCloudStagingList } from '../view/template';
import { CloudStagingListRepository } from '../repository/CloudStagingListRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { CloudStagingListWebComponent } from '..';
import { CloudStagingListEvents, UpdateRings } from '../types';

import { CloudStagingListDataProviderFactory } from '../factory/CloudStagingListDataProviderFactory';
import { GridEvents } from '../../../grid/types/common';
import { State } from '../../../grid/uiElements/inlineRowActions/types';
import { showErrorToast } from '../../../ui/gzToastNotification';
import { getProductChangelogLink } from '../view/productChangelogLinks';
import { htmlEncode } from '../../../utils/escape.utils';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { LinkType } from 'apps/webcomponents/src/ui/gzLinkComponent/types';

export class CloudStagingListController extends BaseWebComponentController {
  public component: CloudStagingListWebComponent;
  public dataRepository: CloudStagingListRepository;
  public view: CloudStagingListView;
  public viewConstructor: typeof CloudStagingListView;
  public dataRepositoryConstructor: typeof CloudStagingListRepository;
  public dataProviderFactory: CloudStagingListDataProviderFactory;
  public dataProvider: CloudStagingListDataProviderFactory;

  constructor(component: CloudStagingListWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: CloudStagingListView,
      viewTemplate: templateCloudStagingList,
      dataRepositoryConstructor: CloudStagingListRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$cloudStagingSideBar,
        event: CloudStagingListEvents.SHOW_OS,
        callback: this.showOs,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EDIT_ROW_SAVE,
        callback: this.onEditRowSave,
      },
    ];
  }

  private async onEditRowSave(event: WebComponentEvent) {
    const currentRowIndex = event.detail.rowData.index;
    const inlineMenu = this.view.dom.$grid.controller.view.getRowInlineMenu(currentRowIndex + 2);
    const { stagingProfile: newStagingProfiles } = event.detail.newData.item;
    const { stagingProfile: oldStagingProfiles = [], version: version, type: type } = event.detail.rowData.item;
    const stagingProfileString =
      newStagingProfiles && newStagingProfiles.length
        ? newStagingProfiles.map(ring => this.dependencies.translateUpdateRing(ring)).join(', ')
        : this.dependencies.translateUpdateRing(UpdateRings.NONE);

    if (this.arrayEquals(newStagingProfiles, oldStagingProfiles)) {
      // do not send an update request with same data
      inlineMenu.state = null;

      if (
        this.view.dom.$grid.controller.view.getAllActiveEditableCellsInGrid().length !== 0 &&
        isEmpty(this.view.dom.$grid.controller.view.getAllActiveEditableCellsInGrid()[0].value)
      ) {
        this.view.dom.$grid.controller.view.getAllActiveEditableCellsInGrid()[0].value = stagingProfileString;
      }

      return;
    }

    try {
      inlineMenu.state = State.LOADING;
      inlineMenu.disabled = true;

      await this.dataRepository.updatePinnedVersion(type, newStagingProfiles, version);

      inlineMenu.disabled = false;
      inlineMenu.state = State.SUCCESS;

      setTimeout(() => {
        inlineMenu.state = null;
      }, 5000);
    } catch (e) {
      inlineMenu.disabled = false;
      inlineMenu.state = State.ERROR;

      showErrorToast(this.dependencies.translateFn('Operation failed due to an internal service error.'));
    } finally {
      this.view.dom.$grid.propertiesSet = true;
    }
  }

  async getDataForView() {
    return this.dataRepository.getDataForView();
  }

  showOs(ev: WebComponentEvent) {
    this.sideNavFilters(ev.detail);
  }

  private sideNavFilters(extraParams) {
    this.view.dom.$grid.controller.view.disableAllGridRowsEditMode();
    this.view.dom.$grid.propertiesSet = false;
    this.view.dom.$grid.extraParams = extraParams;
    this.view.dom.$gridTitle.innerText = this.dependencies.translateEndpointProductTypesTitle(extraParams.type);
    this.view.dom.$gridTitleTooltip.tooltipText = this.dependencies.translateEndpointProductTypesTitle(
      extraParams.type
    );
    const kbLink = getProductChangelogLink(extraParams.type, this.dependencies.canHaveChildCompanies());
    this.view.dom.$changelogText.innerHTML = `<gz-link
        type="${LinkType.UNDERLINE}"
        href="${kbLink}"
        ><span>${htmlEncode(this.dependencies.translateFn('Product changelog'))}</span></gz-link
      >`;
    this.view.dom.$grid.propertiesSet = true;
  }

  public arrayEquals(a: Array<any>, b: Array<any>) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
  }
}
